import type { LocationPoint } from '../planning.ts'
import type {
  TransitArriveByRouteMessage,
  TransitMetadata,
  TransitRouteMessage,
  TransitWorkerMessage,
  TransitWorkerRequest,
} from './protocol.ts'
import type { ExactTransfer, StationAccess, StationEgress, TransferPass } from './router.ts'

interface TransitRouteOptions {
  walkingAccessByOrigin?: StationAccess[][]
  transferPass?: TransferPass
  exactTransfers?: ExactTransfer[]
}

export class TransitClient {
  readonly ready: Promise<TransitMetadata>
  metadata: TransitMetadata | null = null
  unavailableReason: string | null = null

  private worker: Worker | null = null
  private requestId = 0
  private readonly pending = new Map<number, {
    resolve: (message: TransitRouteMessage | TransitArriveByRouteMessage) => void
    reject: (cause: Error) => void
  }>()

  constructor(online = true) {
    this.ready = new Promise((resolve, reject) => {
      if (!online) {
        const error = new Error('Subway schedules require an internet connection.')
        this.unavailableReason = error.message
        reject(error)
        return
      }
      try {
        this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })
      } catch (cause) {
        const error = cause instanceof Error ? cause : new Error('Subway routing is unavailable.')
        this.fail(error)
        reject(error)
        return
      }

      this.worker.addEventListener('message', (event: MessageEvent<TransitWorkerMessage>) => {
        const message = event.data
        if (message.type === 'ready') {
          this.metadata = message.metadata
          resolve(message.metadata)
        } else if (message.type === 'route' || message.type === 'arrive-by') {
          const pending = this.pending.get(message.requestId)
          if (!pending) return
          this.pending.delete(message.requestId)
          pending.resolve(message)
        } else {
          const error = new Error(message.message)
          if (message.requestId === undefined) {
            this.fail(error)
            reject(error)
          } else {
            this.pending.get(message.requestId)?.reject(error)
            this.pending.delete(message.requestId)
          }
        }
      })
      this.worker.addEventListener('error', () => {
        const error = new Error('The subway routing worker stopped unexpectedly.')
        this.fail(error)
        reject(error)
      })
    })
  }

  route(
    date: string,
    departureSeconds: number,
    origins: Pick<LocationPoint, 'lat' | 'lon'>[],
    options: TransitRouteOptions = {},
  ): Promise<TransitRouteMessage['results']> {
    if (!this.worker || !this.metadata) {
      return Promise.reject(new Error(this.unavailableReason ?? 'Subway schedules are still loading.'))
    }
    this.pending.forEach(({ reject }) => reject(new Error('Superseded by a newer route request.')))
    this.pending.clear()
    const requestId = ++this.requestId
    const request: TransitWorkerRequest = {
      type: 'route',
      requestId,
      date,
      departureSeconds,
      origins,
      ...options,
    }
    return new Promise((resolve, reject) => {
      this.pending.set(requestId, {
        resolve: (message) => message.type === 'route'
          ? resolve(message.results)
          : reject(new Error('Subway routing returned an unexpected response.')),
        reject,
      })
      try {
        this.worker?.postMessage(request)
      } catch (cause) {
        this.pending.delete(requestId)
        reject(cause instanceof Error ? cause : new Error('Subway routing could not start.'))
      }
    })
  }

  arriveBy(
    date: string,
    arrivalSeconds: number,
    accessByOrigin: StationAccess[][],
    egressByDestination: StationEgress[][],
    options: Pick<TransitRouteOptions, 'transferPass' | 'exactTransfers'> = {},
  ): Promise<TransitArriveByRouteMessage['results']> {
    if (!this.worker || !this.metadata) {
      return Promise.reject(new Error(this.unavailableReason ?? 'Subway schedules are still loading.'))
    }
    this.pending.forEach(({ reject }) => reject(new Error('Superseded by a newer route request.')))
    this.pending.clear()
    const requestId = ++this.requestId
    const request: TransitWorkerRequest = {
      type: 'arrive-by', requestId, date, arrivalSeconds, accessByOrigin, egressByDestination, ...options,
    }
    return new Promise((resolve, reject) => {
      this.pending.set(requestId, {
        resolve: (message) => message.type === 'arrive-by'
          ? resolve(message.results)
          : reject(new Error('Subway routing returned an unexpected response.')),
        reject,
      })
      try {
        this.worker?.postMessage(request)
      } catch (cause) {
        this.pending.delete(requestId)
        reject(cause instanceof Error ? cause : new Error('Subway routing could not start.'))
      }
    })
  }

  private fail(error: Error): void {
    this.metadata = null
    this.unavailableReason = error.message
    this.worker?.terminate()
    this.worker = null
    this.pending.forEach(({ reject }) => reject(error))
    this.pending.clear()
  }
}
